import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import VenueList from './VenueList';
import VenueForm from './VenueForm';
import VenueDashboard from './VenueDashboard';
import VenueScopedLayout from '../../../components/layout/VenueScopedLayout';
import VenueAccessGuard from '../../../components/auth/VenueAccessGuard';
import FullAdminGuard from '../../../components/auth/FullAdminGuard';
// Locations
import WarehouseList from './warehouses/WarehouseList';
// Wine Lists
import WineListList from './wine-lists/WineListList';
import WineListForm from './wine-lists/WineListForm';
import WineListDetail from './wine-lists/WineListDetail';
import WineListItemForm from './wine-lists/WineListItemForm';
import WineListImport from './wine-lists/WineListImport';
// Menus (Phase 2B — cocktail-only)
import MenuList from './menus/MenuList';
import MenuForm from './menus/MenuForm';
import MenuDetail from './menus/MenuDetail';
import MenuItemForm from './menus/MenuItemForm';
// Menu Consumption Recipes (Phase 2B — INERT storage)
// QR landing page template (venue branding)
import QrLandingTemplateEditor from './qr-landing/QrLandingTemplateEditor';
// Inventory
import InventoryDashboard from './inventory/InventoryDashboard';
import InventoryMovementFeed from './inventory/InventoryMovementFeed';
import InventoryValuation from './inventory/InventoryValuation';
import InventoryItemList from './inventory/InventoryItemList';
import InventoryItemDetail from './inventory/InventoryItemDetail';
import InventoryItemForm from './inventory/InventoryItemForm';
import ManualAdjustmentForm from './inventory/ManualAdjustmentForm';
import InventoryAlerts from './inventory/InventoryAlerts';
import InventoryCategoryManagement from './inventory/InventoryCategoryManagement';
// Purchasing
import PurchaseOrderList from './purchasing/PurchaseOrderList';
import PurchaseOrderForm from './purchasing/PurchaseOrderForm';
import OrderSheetPage from './purchasing/OrderSheetPage';
import PurchaseOrderDetail from './purchasing/PurchaseOrderDetail';
import ReceivingScreen from './purchasing/ReceivingScreen';
import InvoiceList from './purchasing/InvoiceList';
// Stock Counts
import StockCountList from './stock-counts/StockCountList';
import StockCountForm from './stock-counts/StockCountForm';
import StockCountEntry from './stock-counts/StockCountEntry';
import StockCountReview from './stock-counts/StockCountReview';
// Transfers
import TransferList from './transfers/TransferList';
import TransferForm from './transfers/TransferForm';
import TransferEntry from './transfers/TransferEntry';
import TransferReview from './transfers/TransferReview';
// Sales Imports (Phase 5.A.4)
import SalesImportList from './sales-imports/SalesImportList';
import SalesImportUpload from './sales-imports/SalesImportUpload';
import SalesImportDetail from './sales-imports/SalesImportDetail';
// POS Inventory
import PosGovernanceList from './pos-inventory/PosGovernanceList';
import InventoryDriftPage from './inventory/InventoryDriftPage';
import CostLayerDriftPage from './inventory/CostLayerDriftPage';
import InventorySettings from './inventory/InventorySettings';
// Recipes
import RecipeList from './recipes/RecipeList';
import RecipeDetail from './recipes/RecipeDetail';
// Production Orders
import ProductionOrderList from './production-orders/ProductionOrderList';
import ProductionOrderCreate from './production-orders/ProductionOrderCreate';
import ProductionOrderDetail from './production-orders/ProductionOrderDetail';
// Reports (REPORTING-CENTER.1)
import ReportsCenter from './reports/ReportsCenter';
import InventoryMovementsReport from './reports/InventoryMovementsReport';
import TransactionRegister from './reports/TransactionRegister';
import WarehouseConsumptionReport from './reports/WarehouseConsumptionReport';
// Ownership
import OwnershipAccountList from './ownership/OwnershipAccountList';
import OwnershipAccountForm from './ownership/OwnershipAccountForm';
import OwnershipReports from './ownership/OwnershipReports';
// Suppliers (venue-scoped)
import VenueSupplierList from './suppliers/VenueSupplierList';
import VenueSupplierDetail from './suppliers/VenueSupplierDetail';

const VenueRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Global venue management — admin-only. VENUE_USER hitting /admin/venues
          is bounced into their assigned venue (or the picker) by FullAdminGuard. */}
      <Route element={<FullAdminGuard />}>
        <Route path="/" element={<VenueList />} />
        <Route path="/create" element={<VenueForm />} />
        <Route path="/edit/:venueId" element={<VenueForm />} />
      </Route>

      {/* Venue-scoped workspace — VenueAccessGuard reads :venueId and blocks
          venue users who try to reach a venue they aren't assigned to. */}
      <Route path="/:venueId" element={<VenueAccessGuard />}>
        <Route element={<VenueScopedLayout />}>
          <Route index element={<VenueDashboard />} />

        {/* Locations */}
        <Route path="warehouses" element={<WarehouseList />} />
        {/* WAREHOUSE-DETAIL.1 — locations moved to the warehouse that holds
            them. Reaching a shelf by first picking a VENUE was arbitrary for a
            shared cellar — every venue with a grant reached the same shelves —
            and it is what made the venue-scoped create land in the wrong store:
            it never took a warehouse, so "General Stock" added to Alfred's went
            to Main Store.

            Redirected rather than deleted: these URLs are in browser histories
            and bookmarks, and a 404 tells a reader they mistyped something. */}
        <Route path="locations" element={<Navigate to="/admin/stock" replace />} />
        <Route path="locations/*" element={<Navigate to="/admin/stock" replace />} />

        {/* Wine Lists — LEGACY COMPATIBILITY SURFACE.
            Primary workflow now lives under Menus. WineListList is gated as
            a soft-redirect surface (Phase 4.I) and these routes remain only
            so bookmarked URLs and the migration window stay functional. */}
        <Route path="wine-lists" element={<WineListList />} />
        <Route path="wine-lists/create" element={<WineListForm />} />
        <Route path="wine-lists/edit/:wineListId" element={<WineListForm />} />
        <Route path="wine-lists/:wineListId" element={<WineListDetail />} />
        <Route path="wine-lists/:wineListId/add" element={<WineListItemForm />} />
        <Route path="wine-lists/:wineListId/edit/:itemId" element={<WineListItemForm />} />
        <Route path="wine-lists/:wineListId/import" element={<WineListImport />} />

        {/* Menus — canonical menu-family surface (Phase 4.B, 4.H, 4.J).
            Supports kind='COCKTAIL' and kind='WINE' items end-to-end. */}
        <Route path="menus" element={<MenuList />} />
        <Route path="menus/create" element={<MenuForm />} />
        <Route path="menus/edit/:menuId" element={<MenuForm />} />
        <Route path="menus/:menuId" element={<MenuDetail />} />
        <Route path="menus/:menuId/add" element={<MenuItemForm />} />
        <Route path="menus/:menuId/edit/:itemId" element={<MenuItemForm />} />

        {/* Inventory */}
        <Route path="inventory" element={<InventoryItemList />} />
        <Route path="inventory/dashboard" element={<InventoryDashboard />} />
        <Route path="inventory/movements" element={<InventoryMovementFeed />} />
        <Route path="inventory/valuation" element={<InventoryValuation />} />
        <Route path="inventory/create" element={<InventoryItemForm />} />
        <Route path="inventory/adjust" element={<ManualAdjustmentForm />} />
        <Route path="inventory/alerts" element={<InventoryAlerts />} />
        <Route path="inventory/categories" element={<InventoryCategoryManagement />} />
        <Route path="inventory/:itemId" element={<InventoryItemDetail />} />
        <Route path="inventory/:itemId/edit" element={<InventoryItemForm />} />

        {/* Purchasing */}
        <Route path="purchasing" element={<PurchaseOrderList />} />
        <Route path="purchasing/invoices" element={<InvoiceList />} />
        <Route path="purchasing/create" element={<PurchaseOrderForm />} />
        {/* PO-SHEET.1 — door A: supplier-first order sheet. */}
        <Route path="purchasing/order-sheet" element={<OrderSheetPage />} />
        <Route path="purchasing/:poId" element={<PurchaseOrderDetail />} />
        <Route path="purchasing/:poId/edit" element={<PurchaseOrderForm />} />
        <Route path="purchasing/:poId/receive" element={<ReceivingScreen />} />

        {/* Stock Counts */}
        <Route path="stock-counts" element={<StockCountList />} />
        <Route path="stock-counts/create" element={<StockCountForm />} />
        <Route path="stock-counts/:countId" element={<StockCountEntry />} />
        <Route path="stock-counts/:countId/review" element={<StockCountReview />} />

        {/* Transfers */}
        <Route path="transfers" element={<TransferList />} />
        <Route path="transfers/create" element={<TransferForm />} />
        <Route path="transfers/:transferId" element={<TransferEntry />} />
        <Route path="transfers/:transferId/review" element={<TransferReview />} />

        {/* Sales Imports (Phase 5.A.4) — operational document workflow that
            generates SALE InventoryMovements. Not a movement itself. */}
        <Route path="sales-imports" element={<SalesImportList />} />
        <Route path="sales-imports/import" element={<SalesImportUpload />} />
        <Route path="sales-imports/:importId" element={<SalesImportDetail />} />

        {/* POS Inventory — backend gates this admin-only; mirror it on the
            frontend so VENUE_USER doesn't reach a page they can't load. */}
        <Route element={<FullAdminGuard />}>
          <Route path="pos-inventory/governance" element={<PosGovernanceList />} />
          {/* INVENTORY-TRUST.1e — ADMIN_ONLY balance drift + recompute. */}
          <Route path="inventory/drift" element={<InventoryDriftPage />} />
          {/* COSTING.1a — ADMIN_ONLY cost-layer drift + rebuild. */}
          <Route path="inventory/cost-layer-drift" element={<CostLayerDriftPage />} />
          {/* COSTING.1a-web (E6) — ADMIN-only Inventory settings (status only). */}
          <Route path="inventory/settings" element={<InventorySettings />} />
        </Route>

        {/* Production Recipes */}
        <Route path="recipes" element={<RecipeList />} />
        <Route path="recipes/create" element={<RecipeDetail />} />
        <Route path="recipes/:recipeId" element={<RecipeDetail />} />

        {/* Production Orders */}
        <Route path="production-orders" element={<ProductionOrderList />} />
        <Route path="production-orders/create" element={<ProductionOrderCreate />} />
        <Route path="production-orders/:orderId" element={<ProductionOrderDetail />} />

        {/* Reports (REPORTING-CENTER.1) — venue Reporting Center. Cards link to
            existing report surfaces; inventory-movements is the first native
            date-range report. */}
        <Route path="reports" element={<ReportsCenter />} />
        <Route path="reports/inventory-movements" element={<InventoryMovementsReport />} />
        <Route path="reports/transactions" element={<TransactionRegister />} />
        {/* REPORTING.R6 — who consumed a warehouse's stock. */}
        <Route path="reports/warehouse-consumption" element={<WarehouseConsumptionReport />} />

        {/* Ownership */}
        <Route path="ownership-accounts" element={<OwnershipAccountList />} />
        <Route path="ownership-accounts/create" element={<OwnershipAccountForm />} />
        <Route path="ownership-accounts/edit/:accountId" element={<OwnershipAccountForm />} />
        <Route path="ownership-reports" element={<OwnershipReports />} />

        {/* Suppliers (venue-scoped, read-only) */}
        <Route path="suppliers" element={<VenueSupplierList />} />
        <Route path="suppliers/:supplierId" element={<VenueSupplierDetail />} />

        {/* QR landing page template (venue branding) */}
        <Route path="qr-landing-template" element={<QrLandingTemplateEditor />} />
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/admin/venues" replace />} />
    </Routes>
  );
};

export default VenueRoutes;
